import track, { useTracking } from 'react-tracking';
import {
  Heading,
  HeadingRow,
  IdeaName,
  NameDescriptionContainer,
  SavedViewsColumn,
  NewScreener,
} from './styled';
import { Button, Row, Column, Text } from '@simplywallst/ui-core';
import Breadcrumb from '@components/Breadcrumb';
import If from '@/components/If';
import type { UserView } from '../../Screener';
import { Dropdown } from '@components/Dropdown';
import { ROUTE_CREATE_CRITERIA } from '@/constants/routes';
import { useMemo } from 'react';
import { useHomeRoute } from '@/hooks/useHomeRoute';
import FeedbackAlert from '../FeedbackAlert';

interface Props {
  id: string;
  name: string;
  description: string;
  canSaveScreener: boolean;
  isEditingUserSavedScreener: boolean;
  selectedView: string;
  userIdeas: UserView[];
  userId: string;
  onEditNameClick?: () => void;
  onUserViewChange: (id: string, name: string) => void;
}

const Header = ({
  id,
  description,
  isEditingUserSavedScreener,
  name,
  selectedView,
  userIdeas,
  canSaveScreener,
  userId,
  onEditNameClick,
  onUserViewChange,
}: Props) => {
  const { trackEvent } = useTracking();
  const hasName = name !== '';
  const homeRoute = useHomeRoute();
  const breadcrumbList = useMemo(() => {
    return [
      { name: 'Home', dispatch: { type: homeRoute } },
      { name: 'Screener', dispatch: { type: ROUTE_CREATE_CRITERIA } },
    ] as const;
  }, [homeRoute]);
  return (
    <Heading>
      <Row>
        <Column col={6} displayFlex alignItem="center">
          <Breadcrumb list={breadcrumbList} />
        </Column>
        <Column
          col={6}
          displayFlex
          justifyContent="flex-end"
          alignItem="center"
        >
          <If match={id !== '0'}>
            <NewScreener
              styleType="gentle"
              to={{ type: ROUTE_CREATE_CRITERIA }}
              onClick={() => {
                trackEvent({
                  action: 'click',
                  subject: 'new-screener',
                });
              }}
            >
              New Screener
            </NewScreener>
          </If>
        </Column>
      </Row>
      <HeadingRow>
        <Column colMd={9} colSm={12}>
          <If match={!hasName}>
            <IdeaName>Stock Screener</IdeaName>
          </If>
          <If match={hasName}>
            <NameDescriptionContainer>
              <IdeaName data-cy-id="view-name">{name}</IdeaName>
              {description && <p>{description}</p>}
              <If match={canSaveScreener && isEditingUserSavedScreener}>
                <Text typography="small">
                  <Button
                    styleType="strongLink"
                    marginTop
                    onClick={() => {
                      onEditNameClick && onEditNameClick();
                      trackEvent({
                        action: 'click',
                        subject: 'Edit name and description',
                      });
                    }}
                  >
                    Edit name &amp; description
                  </Button>
                </Text>
              </If>
            </NameDescriptionContainer>
          </If>
        </Column>
        <SavedViewsColumn
          colMd={3}
          justifyContent="flex-end"
          justifyContentSm="flex-start"
        >
          <If match={canSaveScreener}>
            <Dropdown
              label="My Saved Screeners"
              name="saved-screeners"
              emptyMessage="You have not saved any screeners"
              placeholder="Select..."
              value={selectedView}
              options={userIdeas}
              onSelect={(id) => {
                try {
                  const index = userIdeas.findIndex((idea) => {
                    return idea.id === id;
                  });
                  onUserViewChange(userIdeas[index].id, userIdeas[index].name);
                } catch (error) {
                  console.error(error);
                }
              }}
              trackingProps={{
                subject: 'my_saved_screeners',
              }}
            />
          </If>
        </SavedViewsColumn>
      </HeadingRow>
      <FeedbackAlert userId={userId} />
    </Heading>
  );
};

export default track({
  type: 'track',
  subject: 'header',
})(Header) as typeof Header;
